import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useMedicationsStore } from '../store'
import { usePetsStore } from '../../pets/store'
import type { MedicationCreate, MedicationFrequency } from '@rondela/shared'

export function MedicationFormPage() {
  const { petId, id } = useParams<{ petId: string; id?: string }>()
  const navigate = useNavigate()
  const { medications, isLoading, error, fetchMedicationsByPet, createMedication, updateMedication, clearError } = useMedicationsStore()
  const { currentPet, fetchPet } = usePetsStore()
  const isEditing = !!id

  const [formData, setFormData] = useState<MedicationCreate>({
    nombre: '',
    dosis: '',
    frecuencia: 'diaria',
    fechaInicio: new Date().toISOString().split('T')[0] ?? '',
    fechaFin: '',
    instrucciones: '',
    horaPreferida: '',
  })

  useEffect(() => {
    if (petId) {
      fetchPet(petId)
      fetchMedicationsByPet(petId)
    }
    return () => clearError()
  }, [petId, fetchPet, fetchMedicationsByPet, clearError])

  useEffect(() => {
    if (id && medications.length > 0) {
      const medication = medications.find((m) => m.id === id)
      if (medication) {
        setFormData({
          nombre: medication.nombre,
          dosis: medication.dosis,
          frecuencia: medication.frecuencia,
          fechaInicio: medication.fechaInicio.split('T')[0] ?? '',
          fechaFin: medication.fechaFin?.split('T')[0] ?? '',
          instrucciones: medication.instrucciones ?? '',
          horaPreferida: medication.horaPreferida ?? '',
        })
      }
    }
  }, [id, medications])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!petId) return

    try {
      if (isEditing && id) {
        await updateMedication(id, formData)
      } else {
        await createMedication(petId, formData)
      }
      navigate(`/mascotas/${petId}`)
    } catch {
      // Error already handled in store
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const frequencyOptions: { value: MedicationFrequency; label: string }[] = [
    { value: 'una_vez', label: 'Una vez' },
    { value: 'diaria', label: 'Diaria' },
    { value: 'cada_12h', label: 'Cada 12 horas' },
    { value: 'cada_8h', label: 'Cada 8 horas' },
    { value: 'semanal', label: 'Semanal' },
    { value: 'mensual', label: 'Mensual' },
    { value: 'segun_necesidad', label: 'Según necesidad' },
  ]

  return (
    <div className="page">
      <div className="page-header">
        <Link to={`/mascotas/${petId}`} className="btn btn-ghost">
          &larr; Volver
        </Link>
        <h1>
          {isEditing ? 'Editar medicamento' : 'Nuevo medicamento'}
          {currentPet && <span className="text-muted"> - {currentPet.nombre}</span>}
        </h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label htmlFor="nombre">Nombre del medicamento *</label>
          <input
            type="text"
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            required
            className="form-control"
            placeholder="Ej: Amoxicilina"
          />
        </div>

        <div className="form-group">
          <label htmlFor="dosis">Dosis *</label>
          <input
            type="text"
            id="dosis"
            name="dosis"
            value={formData.dosis}
            onChange={handleChange}
            required
            className="form-control"
            placeholder="Ej: 500mg, 1 pastilla, 5ml"
          />
        </div>

        <div className="form-group">
          <label htmlFor="frecuencia">Frecuencia *</label>
          <select
            id="frecuencia"
            name="frecuencia"
            value={formData.frecuencia}
            onChange={handleChange}
            className="form-control"
          >
            {frequencyOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaInicio">Fecha de inicio *</label>
            <input
              type="date"
              id="fechaInicio"
              name="fechaInicio"
              value={formData.fechaInicio}
              onChange={handleChange}
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="fechaFin">Fecha de fin</label>
            <input
              type="date"
              id="fechaFin"
              name="fechaFin"
              value={formData.fechaFin}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="horaPreferida">Hora preferida</label>
          <input
            type="time"
            id="horaPreferida"
            name="horaPreferida"
            value={formData.horaPreferida}
            onChange={handleChange}
            className="form-control"
          />
          <p className="form-help">Hora del día en que se debe administrar el medicamento</p>
        </div>

        <div className="form-group">
          <label htmlFor="instrucciones">Instrucciones</label>
          <textarea
            id="instrucciones"
            name="instrucciones"
            value={formData.instrucciones}
            onChange={handleChange}
            rows={3}
            className="form-control"
            placeholder="Ej: Tomar con comida, no mezclar con..."
          />
        </div>

        <div className="form-actions">
          <Link to={`/mascotas/${petId}`} className="btn btn-secondary">
            Cancelar
          </Link>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Agregar medicamento'}
          </button>
        </div>
      </form>
    </div>
  )
}
